import AmplifyEventBus from './plugins/aws-amplify-vue/events/AmplifyEventBus'
import { datadogLogs } from '@datadog/browser-logs'
import { createServiceWorkerHealth } from './utils/serviceWorkerHealth'

if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    const scope = new URL(import.meta.env.BASE_URL, location.origin).href
    const scriptURL = new URL('service-worker.js', scope).href
    // Track SW version in localStorage so we can detect same-version
    // re-activations (e.g. Lighthouse) and skip the reload
    let pendingReload = false
    navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SW_ACTIVATE_STARTED') {
            const newVersion = event.data.version
            const oldVersion = localStorage.getItem('sw-version')
            localStorage.setItem('sw-version', newVersion)
            if (pendingReload) {
                pendingReload = false
                if (newVersion !== oldVersion) {
                    console.log(`SW updated: ${oldVersion} → ${newVersion}, reloading`)
                    localStorage.setItem('genoUpdated', true)
                    AmplifyEventBus.emit('sw', 'reloadPage')
                } else {
                    console.log(`SW re-activated with same version ${newVersion}, skipping reload`)
                }
            }
        }
    })

    // controllerchange fires before SW_ACTIVATE_STARTED, so just
    // set a flag and let the message handler decide whether to reload
    let handled = false
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (handled) return
        handled = true
        pendingReload = true
    })


    const monitor = createServiceWorkerHealth({
        serviceWorker: navigator.serviceWorker, scriptURL, scope,
        online: () => navigator.onLine !== false,
        visible: () => document.visibilityState !== 'hidden',
        report(event, details) {
            const level = event === 'check_failed' ? 'warn' : 'info'
            datadogLogs.logger[level]('Service worker health: ' + event, {
                service_worker: { event, ...details }
            })
        },
        onRegistration(registration) {
            const watched = new Map()
            const watchUpdate = () => {
                if (!registration.active) return
                const worker = registration.installing || registration.waiting
                if (!worker || watched.has(worker)) return
                let activated = false
                const onState = () => {
                    if (worker.state === 'installed' && !activated) {
                        activated = true
                        worker.postMessage({ type: 'SKIP_WAITING' })
                    }
                }
                watched.set(worker, onState)
                worker.addEventListener('statechange', onState)
                onState()
            }
            registration.addEventListener('updatefound', watchUpdate)
            watchUpdate()
            return () => {
                registration.removeEventListener('updatefound', watchUpdate)
                for (const [worker, callback] of watched) worker.removeEventListener('statechange', callback)
            }
        }
    })
    const RECHECK_GAP = 15 * 1000
    // DEPLOYED comes from the build notification (Nav.checkUpdates) and
    // always checks; route changes and foregrounding check at most every
    // few seconds rather than waiting out the five-minute poll.
    const onEvent = event => {
        if (event === 'DEPLOYED') void monitor.check('deploy', { force: true })
        else if (event === 'CHECK_UPDATES') void monitor.check('route', { gap: RECHECK_GAP })
    }
    const onOnline = () => { void monitor.check('online', { force: true }) }
    const onVisible = () => { if (document.visibilityState === 'visible') void monitor.check('visible', { gap: RECHECK_GAP }) }
    AmplifyEventBus.on('sw', onEvent)
    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisible)
    void monitor.check('startup')
}
